import React, { Component, Suspense, useEffect } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { routes } from "@/routes";
import { useUseCaseControls } from "@hooks/useUseCaseControls";
import { useTabs } from "@context/TabsContext";
import { useCurrentTabId } from "@context/CurrentTabContext";
import MenuAccessGuard from "@components/MenuAccessGuard";
import { errorBoxStyle, loadingFallbackStyle, notFoundBoxStyle, retryButtonStyle } from "./TabPageRenderer.styles";

interface Props {
  path: string;
}

// Registra close() no TabsContext para que closeTab() o chame no momento certo.
// Não usa cleanup de useEffect — close() só dispara no clique explícito de fechar aba.
const TabUseCase: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { close } = useUseCaseControls();
  const tabId = useCurrentTabId();
  const { registerCloseCallback, unregisterCloseCallback } = useTabs();

  useEffect(() => {
    if (!tabId) return;
    registerCloseCallback(tabId, close);
    return () => unregisterCloseCallback(tabId);
  }, [tabId, close, registerCloseCallback, unregisterCloseCallback]);

  return <>{children}</>;
};

interface ErrorBoundaryState {
  hasError: boolean;
  message: string;
}

class TabErrorBoundary extends Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, message: "" };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, message: error.message };
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={errorBoxStyle}>
          <Typography variant="body1" color="error" gutterBottom>
            Erro ao carregar esta aba.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {this.state.message}
          </Typography>
          <Button
            sx={retryButtonStyle}
            variant="outlined"
            size="small"
            onClick={() => this.setState({ hasError: false, message: "" })}>
            Tentar novamente
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

const loadingFallback = (
  <Box sx={loadingFallbackStyle}>
    <CircularProgress />
  </Box>
);

const TabPageRenderer: React.FC<Props> = ({ path }) => {
  const route = routes.find((r) => r.path === path);

  if (!route) {
    return <Box sx={notFoundBoxStyle}>Rota não encontrada: {path}</Box>;
  }

  const PageComponent = route.element;
  const Layout = route.layout;

  const page = (
    <Suspense fallback={loadingFallback}>
      <PageComponent />
    </Suspense>
  );

  // A página entra como children do layout. O DocumentsLayout provê o <UseCaseManager>
  // e renderiza esses children, então TabUseCase e a página têm o manager como ancestral.
  const LayoutWithChildren = Layout as React.FC<{ children?: React.ReactNode }>;
  const content = LayoutWithChildren ? (
    <LayoutWithChildren>
      <TabUseCase>{page}</TabUseCase>
    </LayoutWithChildren>
  ) : (
    page
  );

  return (
    <TabErrorBoundary>
      <MenuAccessGuard path={path}>{content}</MenuAccessGuard>
    </TabErrorBoundary>
  );
};

export default TabPageRenderer;
